import { apiSlice, TagType } from "@/features/api/apiSlice.ts"
import {
  type CropPlanting,
  type CropPlantingCreateUpdateRequest,
} from "@/features/crops/types/crop-planting.ts"

const CROP_PLANTINGS_API_BASE_URL = "/crop-plantings/"

interface CropsPlanningFilter {
  parcel?: string
  crop?: string
  is_active?: boolean
}

export const cropPlantingsApi = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    getAllCropPlantings: builder.query<CropPlanting[], CropsPlanningFilter>({
      query: (params) => ({
        url: CROP_PLANTINGS_API_BASE_URL,
        params: { parcel: params.parcel ?? undefined },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: TagType.CropsPlanting, id })),
              { type: TagType.CropsPlanting, id: "LIST" },
            ]
          : [{ type: TagType.CropsPlanting, id: "LIST" }],
    }),
    getCropPlanting: builder.query<CropPlanting, string>({
      query: (id) => `${CROP_PLANTINGS_API_BASE_URL}${id}/`,
      providesTags: (_result, _error, id) => [
        { type: TagType.CropsPlanting, id },
      ],
    }),
    createCropPlanting: builder.mutation<
      CropPlanting,
      CropPlantingCreateUpdateRequest
    >({
      query: (cropPlanting) => ({
        url: CROP_PLANTINGS_API_BASE_URL,
        method: "POST",
        body: cropPlanting,
      }),
      invalidatesTags: (_result, _error, cropPlanting) => [
        { type: TagType.CropsPlanting, id: "LIST" },
        { type: TagType.Parcels, id: cropPlanting.parcel },
        { type: TagType.CropsPlanting, id: "LIST" },
      ],
    }),
    updateCropPlanting: builder.mutation<
      CropPlanting,
      CropPlantingCreateUpdateRequest & { id: string }
    >({
      query: (cropPlanting) => ({
        url: `${CROP_PLANTINGS_API_BASE_URL}${cropPlanting.id}/`,
        method: "PATCH",
        body: cropPlanting,
      }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: TagType.CropsPlanting, id },
        { type: TagType.CropsPlanting, id: "LIST" },
      ],
    }),
    deleteCropPlanting: builder.mutation<void, string>({
      query: (id) => ({
        url: `${CROP_PLANTINGS_API_BASE_URL}${id}/`,
        method: "DELETE",
      }),
      invalidatesTags: (_result, _error, id) => [
        { type: TagType.CropsPlanting, id },
        { type: TagType.CropsPlanting, id: "LIST" },
        { type: TagType.Parcels },
      ],
    }),
  }),
})

export const {
  useGetAllCropPlantingsQuery,
  useGetCropPlantingQuery,
  useCreateCropPlantingMutation,
  useUpdateCropPlantingMutation,
  useDeleteCropPlantingMutation,
} = cropPlantingsApi
